import type { DefLookup } from "../catalog";
import type { Issue, System } from "../types";
import { DEFAULT_AUTO_SIZE_VDROP_TARGET } from "../settings/auto-size-vdrop-default";

// Explicitly opt in external checks and model limitations. New detectors and
// every critical finding stay active unless deliberately reviewed here.
const REVIEWABLE = new Set([
  "fuse-protection-unverified", "bms-control-unverified",
  "source-current-allocation-unverified", "current-model-unverified",
  "battery-isolator-model-unverified", "pv-isolation-unverified",
  "inverter-dc-isolation-unverified", "manufacturer-breaker-unverified",
  "cranking-vdrop-unverified", "monitor-power-unverified", "no-charging",
]);

export function canReviewIssue(issue: Issue): boolean {
  return issue.severity !== "critical" && REVIEWABLE.has(issue.code);
}

export function issueReviewKey(issue: Issue): string {
  const earthIdentity = issue.code === "ac-source-earth-missing" ? issue.message.vars?.earthPair
    : issue.code === "ac-earth-port-ambiguous" ? issue.message.vars?.earthPort : undefined;
  return JSON.stringify([issue.code, [...(issue.nodeIds ?? [])].sort(), [...(issue.cableIds ?? [])].sort(),
    ...(earthIdentity ? [earthIdentity] : [])]);
}

// Exclude presentation only. Keep unknown/new electrical properties by default.
const PRESENTATION = new Set([
  "position", "rotation", "label", "labelColor", "notes", "waypoints",
  "terminalOverrides", "name", "shortName", "subtitle", "description", "image",
  "width", "height", "side", "offset", "pinned", "parentId",
]);
function canonical(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(canonical).join(",")}]`;
  if (value && typeof value === "object") {
    return `{${Object.entries(value).filter(([key, v]) => !PRESENTATION.has(key) && v !== undefined)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([key, v]) => `${JSON.stringify(key)}:${canonical(v)}`).join(",")}}`;
  }
  return JSON.stringify(value) ?? "null";
}

// A compact change detector, not a security signature or electrical approval.
function fingerprint(value: unknown): string {
  const text = canonical(value);
  let a = 2166136261, b = 5381;
  for (let i = 0; i < text.length; i++) {
    a = Math.imul(a ^ text.charCodeAt(i), 16777619);
    b = Math.imul(b, 33) ^ text.charCodeAt(i);
  }
  return `v1:${(a >>> 0).toString(16)}:${(b >>> 0).toString(16)}`;
}

/** Include the whole connected circuit: a remote source or parallel bypass can
 * change a check without changing its highlighted witness. Separate circuits
 * and cosmetic edits do not invalidate a review. */
export function issueReviewFingerprint(
  system: System, issue: Issue, lookup: DefLookup,
  target: number = DEFAULT_AUTO_SIZE_VDROP_TARGET,
): string {
  const nodeIds = new Set(issue.nodeIds ?? []);
  const cableIds = new Set(issue.cableIds ?? []);
  if (!nodeIds.size && !cableIds.size) system.nodes.forEach(n => nodeIds.add(n.id));
  for (const c of system.cables) if (cableIds.has(c.id)) {
    nodeIds.add(c.fromNodeId); nodeIds.add(c.toNodeId);
  }
  let changed = true;
  while (changed) {
    changed = false;
    for (const c of system.cables) if (nodeIds.has(c.fromNodeId) || nodeIds.has(c.toNodeId)) {
      if (!nodeIds.has(c.fromNodeId) || !nodeIds.has(c.toNodeId)) changed = true;
      nodeIds.add(c.fromNodeId); nodeIds.add(c.toNodeId);
    }
  }
  const nodesById = new Map(system.nodes.map(n => [n.id, n]));
  const nodes = system.nodes.filter(n => nodeIds.has(n.id)).sort((a, b) => a.id.localeCompare(b.id));
  const engineZones = (nodeId: string) => {
    const zones: string[] = [];
    const seen = new Set<string>();
    let parent = nodesById.get(nodeId)?.parentId;
    while (parent && !seen.has(parent)) {
      seen.add(parent);
      const zone = nodesById.get(parent);
      if (zone?.kind === "zone" && zone.isEngineBay) zones.push(parent);
      parent = zone?.parentId;
    }
    return zones.sort();
  };
  return fingerprint({
    code: issue.code, severity: issue.severity, target,
    // Omit the legacy default to retain existing review fingerprints; a
    // different or unknown rule revision must reopen the acknowledgement.
    ...(system.designProfileId && system.designProfileId !== "marine-abyc-v1"
      ? { designProfileId: system.designProfileId } : {}),
    // Preserve reason changes without invalidating on translated labels.
    reasons: [issue.message.key, ...(issue.details ?? []).map(d => d.key)].sort(),
    nodes: nodes.map(n => ({ ...n, engineBay: engineZones(n.id), definition: n.kind === "component" ? lookup(n.definitionId, n.customDefinition) : undefined })),
    cables: system.cables.filter(c => nodeIds.has(c.fromNodeId) || nodeIds.has(c.toNodeId)).sort((a, b) => a.id.localeCompare(b.id)),
  });
}

export function isIssueReviewed(system: System, issue: Issue, lookup: DefLookup, target?: number): boolean {
  if (!canReviewIssue(issue)) return false;
  const saved = system.issueReviews?.[issueReviewKey(issue)];
  return typeof saved === "string" && saved === issueReviewFingerprint(system, issue, lookup, target);
}

export function partitionReviewedIssues(system: System, issues: Issue[], lookup: DefLookup, target?: number) {
  const active: Issue[] = [], reviewed: Issue[] = [];
  for (const issue of issues) (isIssueReviewed(system, issue, lookup, target) ? reviewed : active).push(issue);
  return { active, reviewed };
}
